import React from 'react';

import { DEFAULT_PRECISION } from 'helpers/constants';
import colors from 'theme/_colors.scss';
import { IconBell, IconInfoFilled } from 'svgIcons/MotionPortalIcons';
import { LEVEL, LIMIT_MENU_OPTIONS, LIMIT_SYMBOL, RANGE, SLIDER_VALUES, LIMIT_VALUE_REGEX } from './constants';

export const getContextMenuOptions = (props, isOffsetEnabled, options) => {
    const hasAnyBasedOnDefaultLimit = props?.item?.limits?.some((item) => item?.isBasedOnDefault);
    const hasAnyDefaultLimit = props?.item?.limits?.some((item) => item?.isDefault);
    const isModified = props?.item?.isModified || props?.item?.isNew;
    let menuOptions = options;
    const applicableLimits = props?.checkIsSmseAsset && props.isApplicable;
    const hasNoLimits = props?.item?.limits?.length === 0;
    if (!props?.resetToDefaultLimitCapability) { menuOptions = options.filter((item) => item.id !== LIMIT_MENU_OPTIONS.RESET_DEFAULT_LIMITS); }

    if (props?.checkIsSmseAsset && !hasAnyDefaultLimit) {
        menuOptions = menuOptions.filter((item) => item.id !== LIMIT_MENU_OPTIONS.RESET_DEFAULT_LIMITS);
    }

    return menuOptions.map(item => {
        if (item.id === LIMIT_MENU_OPTIONS.REMOVE_SIGNAL) {
            return { ...item, disabled: isModified || hasAnyDefaultLimit || hasAnyBasedOnDefaultLimit || (hasNoLimits ? applicableLimits : hasNoLimits) };
        }
        if (item.id === LIMIT_MENU_OPTIONS.EDIT_OFFSET) {
            return { ...item, disabled: !isOffsetEnabled };
        }
        if (item.id === LIMIT_MENU_OPTIONS.RESET_DEFAULT_LIMITS) {
            if (props?.checkIsSmseAsset) {
                return { ...item, disabled: true };
            }
            const hasDefaultAndModified = hasAnyDefaultLimit && isModified;
            const isEnabledForDrives = hasAnyBasedOnDefaultLimit || hasDefaultAndModified;
            return { ...item, disabled: !isEnabledForDrives };
        }
        return item;
    });
};


export const getSliderValues = (limits) => {
    return limits.map(limit => ({
        type: limit.limitLevel,
        value: limit.limitValue,
        symbol: limit.limitDirection === LEVEL.LowerLimit ? LIMIT_SYMBOL.Lower : LIMIT_SYMBOL.Greater
    }));
};

// export const getUnitsGroupID = (timeseriesNameTextKey, kpiList) => {
//     const timeSeriesList = kpiList.reduce((result, group) => {
//         return result.concat(group.timeseries);
//     }, []);

//     const foundTimeseries = timeSeriesList.find(item => item.timeseriesNameTextKey === timeseriesNameTextKey);

//     return foundTimeseries ? foundTimeseries.timeseriesUnitGroupID : null;
// };

export const getSliderValue = {
    oneDirectionAlarmOrAlert: [
        { type: 'Alarm', value: 75, symbol: '>' },
    ],
    oneDirectionAlarmAndAlert: [
        { type: 'Alert', value: 35, symbol: '>' },
        { type: 'Alarm', value: 75, symbol: '>' },
    ],
    inRangeAlarmOrAlert: [
        { type: 'Alarm', value: 35, symbol: '>' },
        { type: 'Alarm', value: 75, symbol: '<' },
    ],
    outOfRangeAlarmOrAlert: [
        { type: 'Alarm', value: 35, symbol: '<' },
        { type: 'Alarm', value: 75, symbol: '>' },
    ],
    inRangeAlarmAndAlert: [
        { type: 'Alert', value: 35, symbol: '>' },
        { type: 'Alarm', value: 75, symbol: '>' },
        { type: 'Alert', value: 100, symbol: '>' },
        { type: 'Alarm', value: 130, symbol: '<' },
    ],
    outOfRangeAlarmAndAlert: [
        { type: 'Alert', value: 80, symbol: '>' },
        { type: 'Alarm', value: 30, symbol: '<' },
        { type: 'Alarm', value: 100, symbol: '>' },
        { type: 'Alert', value: 50, symbol: '<' },
    ],
};

export const getColumnConfigSuggestedLimits = (translate, timeSeries) => {
    return [
        {
            title: translate('ABB.Powertrain.Frontend.limitType'),
            selectable: false,
            key: 'limitType',
            visible: true
        },
        {
            title: translate('ABB.Powertrain.Frontend.currentLimit'),
            selectable: true,
            key: 'currentLimit',
            visible: timeSeries.limits?.length > 0
        },
        {
            title: translate('ABB.Powertrain.Frontend.suggestedLimit'),
            selectable: true,
            key: 'suggestedLimit',
            visible: true
        }
    ];
};

export const limitTypes = ['currentLimit', 'suggestedLimit'];

export const getLimitValueRegex = (precision = DEFAULT_PRECISION) => {
    const parsedPrecision = Number(precision);
    const safePrecision = Number.isInteger(parsedPrecision) && parsedPrecision >= 0 ? parsedPrecision : DEFAULT_PRECISION;

    if (safePrecision === 0) {
        return /^-?\d{0,15}$/;
    }

    if (safePrecision === 2) {
        return LIMIT_VALUE_REGEX;
    }

    return new RegExp(String.raw`^-?\d{0,15}(\.\d{1,${safePrecision}})?$`);
};

const NOTIFICATION_TYPES = {
    INFO: 'info',
    NOTIFICATION: 'notification'
};

export const getNotificationIcons = (type) => {
    switch (type) {
        case NOTIFICATION_TYPES.INFO:
            return (
                <IconInfoFilled width={16} height={16} color={colors.nero} />
            );
        case NOTIFICATION_TYPES.NOTIFICATION:
            return (
                <IconBell width={16} height={16} />
            );
        default: return null;
    }
};

const adjustMinMax = (data, type) => {
    if (data.length === 0) {
        return null;
    }

    const values = data.map((item) => Number(item.value));

    let minVal = Math.min(...values);
    let maxVal = Math.max(...values);

    // Custom logic to adjust min and max values
    if (minVal === maxVal) {
        if (type === RANGE.Min) {
            minVal -= 1; // Ensuring minimum is adjusted down
        }
        if (type === RANGE.Max) {
            maxVal += 1; // Ensuring maximum is adjusted up
        }
    }

    if (type === RANGE.Min) {
        let adjustedMin =
            minVal <= 0
                ? Math.ceil(minVal * SLIDER_VALUES.MAX)
                : Math.floor(minVal * SLIDER_VALUES.MIN);
        if (minVal === 1 || minVal === 0) {
            adjustedMin = -1;
        }
        return adjustedMin;
    }
    if (type === RANGE.Max) {
        const adjustedMax =
            maxVal < 0
                ? Math.ceil(maxVal * SLIDER_VALUES.MIN)
                : Math.floor(maxVal * SLIDER_VALUES.MAX);
        return adjustedMax + 1;
    }
    return null;
};

export const getMinValue = (timeSeries, type) => {
    if (!timeSeries) {
        return null; // Return null if timeSeries is undefined or null
    }

    const { minValue, maxValue, limits, offset } = timeSeries;
    const defaultLimits = limits?.filter((limit) => !limit?.isUserLimit); // Include only default and custom limits

    if (type === RANGE.Min) {
        return minValue !== null && minValue !== undefined ? minValue + +offset : adjustMinMax(getSliderValues(defaultLimits), RANGE.Min);
    }

    if (type === RANGE.Max) {
        return maxValue !== null && maxValue !== undefined ? maxValue + +offset : adjustMinMax(getSliderValues(defaultLimits), RANGE.Max);
    }

    return null; // Return null for invalid type
};
